import type { AuthorParams } from './useAuthorQuery'
import type { Author } from '~/utils/solr/author'
import { SolrParamsKey } from '#shared/solr'
import { extractLabel } from '#shared/utils/solr-facet'
import { compareLocaleNorwegian } from '@/utils/string'
import { createCountWithFilters } from '~/utils/filter-count'
import { useAuthorQuery } from './useAuthorQuery'
import { sortAuthorCatalogue } from '~/utils/author/catalogue-sort'

export const AUTHORS_PER_PAGE = 64

interface AuthorFilterState {
  searchKeyword: string
  selectedLegalArea: { value: string; label: string }[]
}

type AuthorDrawerOverrides = Omit<AuthorFilterState, 'searchKeyword'>

export function authorMatches(author: Author, state: AuthorFilterState): boolean {
  if (!matchesAllSearchTerms(state.searchKeyword, [author.name])) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const authorAreas = new Set(author.legalAreas)
    if (!state.selectedLegalArea.some((area) => authorAreas.has(area.value))) {
      return false
    }
  }
  return true
}

function useAuthorFilterState() {
  const page = useState<number>('author-filter-page', () => 1)
  const searchKeyword = useState<string>('author-filter-search', () => '')
  const selectedLegalArea = useState<{ value: string; label: string }[]>(
    'author-filter-legal-area',
    () => [],
  )
  const searchLegalArea = ref<string>('')

  return { page, searchKeyword, selectedLegalArea, searchLegalArea }
}

function useAuthorSource() {
  const solrParams = computed<AuthorParams>(() => ({
    [SolrParamsKey.Authors]: {
      nodeType: 'author',
      sort: 'surname ASC',
      filter: 'bodytext:[* TO *]',
    },
  }))
  const { data: authorsData, isLoading, suspense } = useAuthorQuery(solrParams)

  return { authorsData, isLoading, suspense }
}

function buildLegalAreaOptions(docs: Author[]): { value: string; label: string }[] {
  const allLegalAreas = new Set<string>()

  for (const doc of docs) {
    for (const area of doc.legalAreas) {
      allLegalAreas.add(area)
    }
  }

  return Array.from(allLegalAreas)
    .map((area) => ({ value: area, label: extractLabel(area) }))
    .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
}

function useAuthorResults(
  source: ReturnType<typeof useAuthorSource>,
  state: ReturnType<typeof useAuthorFilterState>,
) {
  const { page, searchKeyword, selectedLegalArea } = state
  const { authorsData } = source
  const allAuthors = computed(() => authorsData.value?.response.docs ?? [])
  const legalAreaOptions = computed(() => buildLegalAreaOptions(allAuthors.value))
  const committedState = computed<AuthorFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    selectedLegalArea: selectedLegalArea.value,
  }))
  const filteredAuthors = computed(() => {
    return allAuthors.value.filter((author) => authorMatches(author, committedState.value))
  })
  const countForFacet = createCountWithFilters(allAuthors, committedState, authorMatches)
  const {
    selected: legalAreaSelected,
    toggle: toggleLegalArea,
    facetOptions: legalAreaFacetOptions,
  } = useFacetFilter(selectedLegalArea, legalAreaOptions, (option) =>
    countForFacet({ selectedLegalArea: [option] }),
  )
  const sorted = computed(() => sortAuthorCatalogue(filteredAuthors.value, searchKeyword.value))
  const paginatedAuthors = computed(() => {
    const startIndex = (page.value - 1) * AUTHORS_PER_PAGE
    return sorted.value.slice(startIndex, startIndex + AUTHORS_PER_PAGE)
  })
  const numFound = computed(() => filteredAuthors.value.length)
  watch([searchKeyword, selectedLegalArea], () => (page.value = 1))

  return {
    legalAreaOptions,
    legalAreaSelected,
    toggleLegalArea,
    legalAreaFacetOptions,
    authors: paginatedAuthors,
    numFound,
    countWithFilters: (overrides: AuthorDrawerOverrides) => countForFacet(overrides),
  }
}

export function _useAuthorFilters() {
  const state = useAuthorFilterState()
  const source = useAuthorSource()
  const results = useAuthorResults(source, state)

  return {
    ...state,
    ...results,
    isLoading: source.isLoading,
    suspense: source.suspense,
    setPage: (newPage: number) => (state.page.value = newPage),
  }
}

export const useAuthorFilters = createSharedComposable(_useAuthorFilters)
